/**
 * Join/leave sound settings for conversation calls and Space voice channels.
 * Device-scoped (localStorage) and independent of the notification-sound toggle.
 */

import { forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/Card';
import { SoundPickerFields, type SoundPickerFieldsLabels } from '../../components/SoundPickerFields';
import type { NotificationSoundId } from '../../constants/notificationSoundPreferenceShared';
import {
  CALL_PRESENCE_EVENTS,
  type CallPresenceEvent,
  setCallPresenceSoundPref,
} from '../../hooks/callPresenceSoundStorage';
import { useCallPresenceSoundPreference } from '../../hooks/useNotificationSoundPreference';
import '../../styles/_call-presence-sounds-card.scss';

const PREFIX = 'account.settings.notifications.callPresence';

function EventSection({ event }: { event: CallPresenceEvent }) {
  const { t } = useTranslation();
  const prefs = useCallPresenceSoundPreference(event);

  const set = useCallback(
    (patch: Parameters<typeof setCallPresenceSoundPref>[1]) => setCallPresenceSoundPref(event, patch),
    [event]
  );
  const setSoundId = useCallback((soundId: NotificationSoundId) => set({ soundId }), [set]);
  const setFriendSoundId = useCallback(
    (friendSoundId: NotificationSoundId) => set({ friendSoundId }),
    [set]
  );

  const pickerLabels = (kind: 'sound' | 'friendSound'): SoundPickerFieldsLabels => ({
    select: t(`${PREFIX}.${kind}Label`),
    preview: t(`${PREFIX}.preview`),
    volume: t(`${PREFIX}.${kind}VolumeLabel`),
    customFile: t(`${PREFIX}.customFile`),
    browse: t(`${PREFIX}.browse`),
    fileMissing: t(`${PREFIX}.fileMissing`),
  });

  return (
    <fieldset className="call-presence-group" data-event={event}>
      <legend className="app-settings-toggle-title">{t(`${PREFIX}.${event}.title`)}</legend>

      <label className="app-settings-toggle">
        <input
          type="checkbox"
          checked={prefs.enabled}
          onChange={(e) => set({ enabled: e.target.checked })}
          data-testid={`call-presence-${event}-enabled`}
        />
        <span className="app-settings-toggle-label">
          <span className="app-settings-toggle-title">{t(`${PREFIX}.${event}.toggle`)}</span>
          <span className="app-settings-toggle-hint">{t(`${PREFIX}.${event}.hint`)}</span>
        </span>
      </label>

      {prefs.enabled && (
        <div className="call-presence-fields">
          <label className="app-settings-toggle">
            <input
              type="checkbox"
              checked={prefs.includeSelf}
              onChange={(e) => set({ includeSelf: e.target.checked })}
              data-testid={`call-presence-${event}-include-self`}
            />
            <span className="app-settings-toggle-label">
              <span className="app-settings-toggle-title">{t(`${PREFIX}.${event}.includeSelfToggle`)}</span>
              <span className="app-settings-toggle-hint">{t(`${PREFIX}.${event}.includeSelfHint`)}</span>
            </span>
          </label>

          <SoundPickerFields
            idPrefix={`call-presence-${event}`}
            soundId={prefs.soundId}
            customPath={prefs.customPath}
            volume={prefs.volume}
            labels={pickerLabels('sound')}
            onSoundIdChange={setSoundId}
            onCustomPathPicked={(customPath) => set({ customPath, soundId: 'custom' })}
            onVolumeChange={(volume) => set({ volume })}
          />

          <label className="app-settings-toggle">
            <input
              type="checkbox"
              checked={prefs.friendEnabled}
              onChange={(e) => set({ friendEnabled: e.target.checked })}
              data-testid={`call-presence-${event}-friend-enabled`}
            />
            <span className="app-settings-toggle-label">
              <span className="app-settings-toggle-title">{t(`${PREFIX}.friendToggle`)}</span>
              <span className="app-settings-toggle-hint">{t(`${PREFIX}.${event}.friendHint`)}</span>
            </span>
          </label>

          {prefs.friendEnabled && (
            <div className="call-presence-friend-fields" data-testid={`call-presence-${event}-friend-fields`}>
              <SoundPickerFields
                idPrefix={`call-presence-${event}-friend`}
                soundId={prefs.friendSoundId}
                customPath={prefs.friendCustomPath}
                volume={prefs.friendVolume}
                labels={pickerLabels('friendSound')}
                onSoundIdChange={setFriendSoundId}
                onCustomPathPicked={(friendCustomPath) =>
                  set({ friendCustomPath, friendSoundId: 'custom' })
                }
                onVolumeChange={(friendVolume) => set({ friendVolume })}
              />
            </div>
          )}
        </div>
      )}
    </fieldset>
  );
}

export const CallPresenceSoundsCard = forwardRef<HTMLDivElement>(function CallPresenceSoundsCard(_props, ref) {
  const { t } = useTranslation();
  return (
    <Card
      variant="elevated"
      className="slide-up app-settings-card app-settings-card-sound"
      ref={ref}
      data-section="call-presence-sounds"
    >
      <h2 className="app-settings-section-title">{t(`${PREFIX}.sectionTitle`)}</h2>
      <p className="app-settings-section-desc">{t(`${PREFIX}.sectionDescription`)}</p>
      {CALL_PRESENCE_EVENTS.map((event) => (
        <EventSection key={event} event={event} />
      ))}
    </Card>
  );
});
